import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Users, CreditCard, Ticket, LayoutDashboard, Loader2, Plus } from "lucide-react";
import { api } from "../api.js";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.js";
import { Button } from "../components/ui/button.js";
import { Badge } from "../components/ui/badge.js";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs.js";
import { Input } from "../components/ui/input.js";
import { Label } from "../components/ui/label.js";

interface Stats {
  totalUsers: number;
  totalSuscripciones: number;
  suscripcionesActivas: number;
  totalCupones: number;
  totalPagos: number;
  montoTotalPagos: number;
}

interface AdminUser {
  id: number;
  email: string;
  nombre: string | null;
  role: string;
  activo: boolean;
  createdAt: string;
  _count: { suscripciones: number; contribuyentes: number };
}

interface AdminSuscripcion {
  id: number;
  estado: string;
  fechaInicio: string | null;
  fechaFin: string | null;
  autoRenueva: boolean;
  mpPreapprovalId: string | null;
  createdAt: string;
  user: { id: number; email: string; nombre: string | null };
  plan: { id: number; nombre: string; precio: number; periodo: string };
  pagos: { id: number; monto: number; estado: string; fechaPago: string | null }[];
}

interface AdminPlan {
  id: number;
  nombre: string;
  descripcion: string | null;
  precio: number;
  periodo: string;
  activo: boolean;
  _count: { suscripciones: number };
}

interface AdminPago {
  id: number;
  mpPaymentId: string | null;
  monto: number;
  moneda: string;
  estado: string;
  fechaPago: string | null;
  createdAt: string;
  suscripcion: {
    id: number;
    user: { id: number; email: string };
    plan: { nombre: string };
  };
}

export default function AdminPage() {
  const [stats, setStats] = useState<Stats | null>(null);
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [suscripciones, setSuscripciones] = useState<AdminSuscripcion[]>([]);
  const [planes, setPlanes] = useState<AdminPlan[]>([]);
  const [pagos, setPagos] = useState<AdminPago[]>([]);
  const [cupones, setCupones] = useState<{ id: number; codigo: string; tipo: string; valor: number; maxUsos: number; usosActuales: number; activo: boolean }[]>([]);
  const [loading, setLoading] = useState(true);
  const [newPlan, setNewPlan] = useState({ nombre: "", descripcion: "", precio: "", periodo: "MENSUAL" });
  const [newCupon, setNewCupon] = useState({ codigo: "", tipo: "MESES_GRATIS", valor: "1", maxUsos: "1" });

  const fetchAll = useCallback(async () => {
    try {
      const [s, u, sus, p, pg, c] = await Promise.all([
        api<Stats>("/admin/stats"),
        api<{ users: AdminUser[] }>("/admin/users"),
        api<{ suscripciones: AdminSuscripcion[] }>("/admin/suscripciones"),
        api<{ planes: AdminPlan[] }>("/admin/planes"),
        api<{ pagos: AdminPago[] }>("/admin/pagos"),
        api<{ cupones: { id: number; codigo: string; tipo: string; valor: number; maxUsos: number; usosActuales: number; activo: boolean }[] }>("/cupones"),
      ]);
      setStats(s);
      setUsers(u.users);
      setSuscripciones(sus.suscripciones);
      setPlanes(p.planes);
      setPagos(pg.pagos);
      setCupones(c.cupones);
    } catch {
      toast.error("Error al cargar datos admin");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleToggleUser = async (user: AdminUser) => {
    try {
      await api(`/admin/users/${user.id}`, {
        method: "PATCH",
        body: JSON.stringify({ activo: !user.activo }),
      });
      fetchAll();
      toast.success(`Usuario ${user.activo ? "desactivado" : "activado"}`);
    } catch {
      toast.error("Error");
    }
  };

  const handleToggleAdmin = async (user: AdminUser) => {
    try {
      await api(`/admin/users/${user.id}`, {
        method: "PATCH",
        body: JSON.stringify({ role: user.role === "ADMIN" ? "USER" : "ADMIN" }),
      });
      fetchAll();
      toast.success("Rol actualizado");
    } catch {
      toast.error("Error");
    }
  };

  const handleCreatePlan = async () => {
    if (!newPlan.nombre || !newPlan.precio) return;
    try {
      await api("/admin/planes", {
        method: "POST",
        body: JSON.stringify({
          nombre: newPlan.nombre,
          descripcion: newPlan.descripcion || undefined,
          precio: parseInt(newPlan.precio),
          periodo: newPlan.periodo,
        }),
      });
      setNewPlan({ nombre: "", descripcion: "", precio: "", periodo: "MENSUAL" });
      fetchAll();
      toast.success("Plan creado");
    } catch {
      toast.error("Error al crear plan");
    }
  };

  const handleCreateCupon = async () => {
    if (!newCupon.codigo) return;
    try {
      await api("/cupones", {
        method: "POST",
        body: JSON.stringify({
          codigo: newCupon.codigo,
          tipo: newCupon.tipo,
          valor: parseInt(newCupon.valor),
          maxUsos: parseInt(newCupon.maxUsos),
        }),
      });
      setNewCupon({ codigo: "", tipo: "MESES_GRATIS", valor: "1", maxUsos: "1" });
      fetchAll();
      toast.success("Cupón creado");
    } catch {
      toast.error("Error al crear cupón");
    }
  };

  const handleDeleteCupon = async (id: number) => {
    if (!window.confirm("¿Desactivar cupón?")) return;
    try {
      await api(`/cupones/${id}`, { method: "DELETE" });
      fetchAll();
      toast.success("Cupón desactivado");
    } catch {
      toast.error("Error");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Panel Admin</h1>
        <p className="text-muted-foreground">Gestión de usuarios, suscripciones, cupones y planes</p>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
          <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{stats.totalUsers}</div><p className="text-xs text-muted-foreground">Usuarios</p></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{stats.suscripcionesActivas}</div><p className="text-xs text-muted-foreground">Suscripciones activas</p></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{stats.totalSuscripciones}</div><p className="text-xs text-muted-foreground">Total suscripciones</p></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{stats.totalCupones}</div><p className="text-xs text-muted-foreground">Cupones</p></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="text-2xl font-bold">{stats.totalPagos}</div><p className="text-xs text-muted-foreground">Pagos aprobados</p></CardContent></Card>
          <Card><CardContent className="pt-6"><div className="text-2xl font-bold">${stats.montoTotalPagos.toLocaleString("es-CL")}</div><p className="text-xs text-muted-foreground">Monto total CLP</p></CardContent></Card>
        </div>
      )}

      <Tabs defaultValue="users">
        <TabsList>
          <TabsTrigger value="users"><Users className="h-4 w-4 mr-2" />Usuarios</TabsTrigger>
          <TabsTrigger value="suscripciones"><CreditCard className="h-4 w-4 mr-2" />Suscripciones</TabsTrigger>
          <TabsTrigger value="planes"><LayoutDashboard className="h-4 w-4 mr-2" />Planes</TabsTrigger>
          <TabsTrigger value="cupones"><Ticket className="h-4 w-4 mr-2" />Cupones</TabsTrigger>
          <TabsTrigger value="pagos"><CreditCard className="h-4 w-4 mr-2" />Pagos</TabsTrigger>
        </TabsList>

        {/* Users */}
        <TabsContent value="users">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-2">
                {users.map((u) => (
                  <div key={u.id} className="flex items-center justify-between border-b pb-2">
                    <div>
                      <span className="font-medium">{u.email}</span>
                      {u.nombre && <span className="text-muted-foreground ml-2">({u.nombre})</span>}
                      <div className="text-xs text-muted-foreground">
                        {u._count.suscripciones} susc · {u._count.contribuyentes} contrib · {new Date(u.createdAt).toLocaleDateString("es-CL")}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={u.role === "ADMIN" ? "default" : "secondary"}>{u.role}</Badge>
                      <Badge variant={u.activo ? "default" : "destructive"}>{u.activo ? "Activo" : "Inactivo"}</Badge>
                      <Button size="sm" variant="outline" onClick={() => handleToggleAdmin(u)}>
                        {u.role === "ADMIN" ? "Quitar admin" : "Hacer admin"}
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleToggleUser(u)}>
                        {u.activo ? "Desactivar" : "Activar"}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Suscripciones */}
        <TabsContent value="suscripciones">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-2">
                {suscripciones.map((s) => (
                  <div key={s.id} className="border-b pb-2">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="font-medium">{s.user.email}</span>
                        <span className="text-muted-foreground ml-2">— {s.plan.nombre}</span>
                      </div>
                      <Badge variant={s.estado === "ACTIVA" ? "default" : s.estado === "CANCELADA" || s.estado === "EXPIRADA" ? "destructive" : "secondary"}>
                        {s.estado}
                      </Badge>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      ${s.plan.precio.toLocaleString("es-CL")} CLP / {s.plan.periodo.toLowerCase()}
                      {s.fechaFin && ` · vence ${new Date(s.fechaFin).toLocaleDateString("es-CL")}`}
                      {s.autoRenueva && " · auto-renueva"}
                      {s.pagos.length > 0 && ` · ${s.pagos.length} pago(s)`}
                    </div>
                  </div>
                ))}
                {suscripciones.length === 0 && <p className="text-muted-foreground">No hay suscripciones</p>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Planes */}
        <TabsContent value="planes">
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="space-y-2">
                {planes.map((p) => (
                  <div key={p.id} className="flex items-center justify-between border-b pb-2">
                    <div>
                      <span className="font-medium">{p.nombre}</span>
                      <span className="text-muted-foreground ml-2">${p.precio.toLocaleString("es-CL")} CLP / {p.periodo.toLowerCase()}</span>
                      <div className="text-xs text-muted-foreground">{p._count.suscripciones} suscripciones</div>
                    </div>
                    <Badge variant={p.activo ? "default" : "destructive"}>{p.activo ? "Activo" : "Inactivo"}</Badge>
                  </div>
                ))}
              </div>
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">Crear plan</h4>
                <div className="grid gap-2 md:grid-cols-4">
                  <Input placeholder="Nombre" value={newPlan.nombre} onChange={(e) => setNewPlan({ ...newPlan, nombre: e.target.value })} />
                  <Input placeholder="Precio CLP" type="number" value={newPlan.precio} onChange={(e) => setNewPlan({ ...newPlan, precio: e.target.value })} />
                  <select className="border rounded px-2" value={newPlan.periodo} onChange={(e) => setNewPlan({ ...newPlan, periodo: e.target.value })}>
                    <option value="MENSUAL">Mensual</option>
                    <option value="ANUAL">Anual</option>
                  </select>
                  <Button onClick={handleCreatePlan}><Plus className="h-4 w-4 mr-1" />Crear</Button>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Cupones */}
        <TabsContent value="cupones">
          <Card>
            <CardContent className="pt-6 space-y-4">
              <div className="space-y-2">
                {cupones.map((c) => (
                  <div key={c.id} className="flex items-center justify-between border-b pb-2">
                    <div>
                      <span className="font-mono font-medium">{c.codigo}</span>
                      <span className="text-muted-foreground ml-2">{c.tipo} ({c.valor})</span>
                      <div className="text-xs text-muted-foreground">{c.usosActuales}/{c.maxUsos} usos</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant={c.activo ? "default" : "destructive"}>{c.activo ? "Activo" : "Inactivo"}</Badge>
                      {c.activo && <Button size="sm" variant="destructive" onClick={() => handleDeleteCupon(c.id)}>Desactivar</Button>}
                    </div>
                  </div>
                ))}
                {cupones.length === 0 && <p className="text-muted-foreground">No hay cupones</p>}
              </div>
              <div className="border-t pt-4">
                <h4 className="font-semibold mb-2">Crear cupón</h4>
                <div className="grid gap-2 md:grid-cols-4">
                  <Input placeholder="Código" value={newCupon.codigo} onChange={(e) => setNewCupon({ ...newCupon, codigo: e.target.value })} />
                  <select className="border rounded px-2" value={newCupon.tipo} onChange={(e) => setNewCupon({ ...newCupon, tipo: e.target.value })}>
                    <option value="MESES_GRATIS">Meses gratis</option>
                    <option value="PORCENTAJE">Porcentaje</option>
                    <option value="MONTO_FIJO">Monto fijo</option>
                    <option value="GRATIS_PERMANENTE">Gratis permanente</option>
                  </select>
                  <Input placeholder="Valor" type="number" value={newCupon.valor} onChange={(e) => setNewCupon({ ...newCupon, valor: e.target.value })} />
                  <Input placeholder="Max usos" type="number" value={newCupon.maxUsos} onChange={(e) => setNewCupon({ ...newCupon, maxUsos: e.target.value })} />
                </div>
                <Button className="mt-2" onClick={handleCreateCupon}><Plus className="h-4 w-4 mr-1" />Crear cupón</Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pagos */}
        <TabsContent value="pagos">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-2">
                {pagos.map((p) => (
                  <div key={p.id} className="flex items-center justify-between border-b pb-2">
                    <div>
                      <span className="font-medium">${p.monto.toLocaleString("es-CL")} {p.moneda}</span>
                      <span className="text-muted-foreground ml-2">{p.suscripcion.user.email}</span>
                      <div className="text-xs text-muted-foreground">
                        {p.suscripcion.plan.nombre} · {new Date(p.createdAt).toLocaleDateString("es-CL")}
                        {p.mpPaymentId && ` · MP: ${p.mpPaymentId}`}
                      </div>
                    </div>
                    <Badge variant={p.estado === "APROBADO" ? "default" : "secondary"}>{p.estado}</Badge>
                  </div>
                ))}
                {pagos.length === 0 && <p className="text-muted-foreground">No hay pagos</p>}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
